import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useTranslation } from '../hooks/useTranslation';
import { usePushNotifications } from '../hooks/usePushNotifications';
import PushPermissionModal from '../components/PushPermissionModal';
import {
  ArrowLeft, Plus, Clock, CheckCircle2, RotateCcw, Ban, X, Edit2,
  ChevronDown, ChevronUp, CalendarClock, AlertCircle, Copy
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';

const STATUS_CONFIG = {
  a_venir: { label: 'À venir', cls: 'bg-atlasCyan/10 text-atlasCyan border-atlasCyan/30' },
  en_cours: { label: 'En cours', cls: 'bg-atlasCyan/20 text-atlasCyan border-atlasCyan/40 animate-pulse' },
  complete: { label: 'Complété', cls: 'bg-green-500/10 text-green-400 border-green-500/30' },
  reporte: { label: 'Reporté', cls: 'bg-orange-500/10 text-orange-400 border-orange-500/30' },
  ignore: { label: 'Ignoré', cls: 'bg-atlasTextSecondary/10 text-atlasTextSecondary border-atlasBorder' },
  annule: { label: 'Annulé', cls: 'bg-red-500/10 text-red-400 border-red-500/30' },
};

const formatDateFR = (dateStr) => {
  try {
    const d = new Date(dateStr + 'T00:00:00');
    return d.toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long' });
  } catch { return dateStr; }
};

const Engagements = () => {
  const navigate = useNavigate();
  const { api } = useAuth();
  const { t } = useTranslation();
  const [data, setData] = useState({ today: [], upcoming: [], history: [] });
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [showDetail, setShowDetail] = useState(null);
  const [expandHistory, setExpandHistory] = useState(false);

  // Form state
  const [form, setForm] = useState({ description: '', date_engagement: '', heure_debut: '08:00', heure_fin: '', rappel_actif: true, justification_vocale_active: true });
  const [editId, setEditId] = useState(null);
  const [saving, setSaving] = useState(false);

  // Push notifications (Phase B1)
  const push = usePushNotifications(api);
  const [showPushModal, setShowPushModal] = useState(false);
  const [pushSubscribing, setPushSubscribing] = useState(false);

  useEffect(() => { fetchData(); }, []);

  const fetchData = async () => {
    try {
      const res = await api.get('/api/engagements');
      const all = [
        ...(res.data.today || []),
        ...(res.data.upcoming || []),
        ...(res.data.history || []),
      ];

      // === Frontend filtering — logique stricte demandée ===
      const today = new Date();
      today.setHours(0, 0, 0, 0);

      const closedStatuts = ['complete', 'reporte', 'ignore', 'annule'];
      const todayList = [];
      const upcomingList = [];
      const historyList = [];

      for (const eng of all) {
        const engDate = new Date(eng.date_engagement + 'T00:00:00');
        engDate.setHours(0, 0, 0, 0);

        if (closedStatuts.includes(eng.statut)) {
          historyList.push(eng);
        } else if (engDate.getTime() === today.getTime()) {
          todayList.push(eng);
        } else if (engDate.getTime() > today.getTime()) {
          upcomingList.push(eng);
        } else {
          // date passée mais statut encore actif → on classe en historique
          historyList.push(eng);
        }
      }

      // Tri : Aujourd'hui & À venir par date+heure asc, Historique par date desc
      const sortAsc = (a, b) => (a.date_engagement + (a.heure_debut || '')).localeCompare(b.date_engagement + (b.heure_debut || ''));
      const sortDesc = (a, b) => (b.date_engagement + (b.heure_debut || '')).localeCompare(a.date_engagement + (a.heure_debut || ''));
      todayList.sort(sortAsc);
      upcomingList.sort(sortAsc);
      historyList.sort(sortDesc);

      console.log('=== DEBUG ENGAGEMENTS ===');
      console.log('Date du jour (local 00:00):', today.toISOString(), '| timestamp:', today.getTime());
      console.log('Total engagements reçus:', all.length);
      console.log('Backend → today:', res.data.today?.length, '| upcoming:', res.data.upcoming?.length, '| history:', res.data.history?.length);
      console.log('Frontend filtré → today:', todayList.length, todayList.map(e => `${e.date_engagement} [${e.statut}]`));
      console.log('Frontend filtré → upcoming:', upcomingList.length, upcomingList.map(e => `${e.date_engagement} [${e.statut}]`));
      console.log('Frontend filtré → history:', historyList.length, historyList.map(e => `${e.date_engagement} [${e.statut}]`));

      setData({ today: todayList, upcoming: upcomingList, history: historyList });
    } catch (e) { console.error('Fetch engagements error:', e); }
    finally { setLoading(false); }
  };

  // Bouton retour : si historique déplié → on le referme ; sinon → on quitte vers /dashboard
  const handleBack = () => {
    if (expandHistory) {
      setExpandHistory(false);
    } else {
      navigate('/dashboard');
    }
  };

  const tomorrow = () => { const d = new Date(); d.setDate(d.getDate() + 1); return d.toISOString().split('T')[0]; };

  const openCreate = (prefill = null) => {
    setForm(prefill || { description: '', date_engagement: tomorrow(), heure_debut: '08:00', heure_fin: '', rappel_actif: true, justification_vocale_active: true });
    setEditId(null);
    setShowCreate(true);
  };

  const openEdit = (eng) => {
    setForm({ description: eng.description, date_engagement: eng.date_engagement, heure_debut: eng.heure_debut, heure_fin: eng.heure_fin || '', rappel_actif: eng.rappel_actif, justification_vocale_active: eng.justification_vocale_active });
    setEditId(eng.id);
    setShowCreate(true);
    setShowDetail(null);
  };

  const handleSave = async () => {
    if (form.description.trim().length < 5) { toast.error(t('engagements.descTooShort')); return; }
    if (!form.date_engagement || !form.heure_debut) { toast.error(t('engagements.missingFields')); return; }
    if (form.heure_fin && form.heure_fin <= form.heure_debut) { toast.error(t('engagements.endBeforeStart')); return; }
    setSaving(true);
    try {
      const payload = { ...form, heure_fin: form.heure_fin || null };
      const isCreate = !editId;
      if (editId) {
        await api.put(`/api/engagements/${editId}`, payload);
        toast.success(t('engagements.updated'));
      } else {
        await api.post('/api/engagements', payload);
        toast.success(t('engagements.created'));
      }
      setShowCreate(false);
      setEditId(null);
      await fetchData();

      // --- Phase B1 : prompt push permission after the VERY FIRST engagement creation ---
      if (isCreate) {
        const dismissed = localStorage.getItem('atlas_push_prompt_dismissed') === '1';
        if (push.isSupported && push.permission === 'default' && !push.isSubscribed && !dismissed) {
          setShowPushModal(true);
        }
      }
    } catch (e) { toast.error(t('common.error')); }
    finally { setSaving(false); }
  };

  const handleEnablePush = async () => {
    setPushSubscribing(true);
    const res = await push.subscribe();
    setPushSubscribing(false);
    if (res.ok) {
      toast.success('✓ Notifications activées');
      setShowPushModal(false);
    } else if (res.reason === 'denied') {
      toast.info('Pas de souci. Tu pourras les activer depuis ton Profil > Notifications & Rituels.');
      localStorage.setItem('atlas_push_prompt_dismissed', '1');
      setShowPushModal(false);
    } else {
      toast.error('Impossible d\'activer les notifications');
    }
  };

  const handleLaterPush = () => {
    localStorage.setItem('atlas_push_prompt_dismissed', '1');
    setShowPushModal(false);
    toast.info('Pas de souci. Tu pourras les activer depuis ton Profil > Notifications & Rituels.');
  };

  const updateStatus = async (id, statut) => {
    try {
      await api.put(`/api/engagements/${id}/status`, { statut });
      toast.success(t('engagements.statusUpdated'));
      setShowDetail(null);
      fetchData();
    } catch { toast.error(t('common.error')); }
  };

  const EngCard = ({ eng, section }) => {
    const st = STATUS_CONFIG[eng.statut] || STATUS_CONFIG.a_venir;
    return (
      <button onClick={() => setShowDetail(eng)} data-testid={`eng-card-${eng.id}`}
        className={`w-full text-left glass-card p-4 hover:bg-white/[0.02] transition-colors ${section === 'today' ? 'border-atlasCyan/20' : ''}`}>
        <div className="flex items-start justify-between gap-2">
          <div className="flex-1 min-w-0">
            <p className="text-xs text-atlasTextSecondary">{formatDateFR(eng.date_engagement)} · {eng.heure_debut}{eng.heure_fin ? ` - ${eng.heure_fin}` : ''}</p>
            <p className="text-white font-medium mt-1 text-sm">{eng.description}</p>
          </div>
          <span className={`text-xs px-2 py-0.5 rounded-full border whitespace-nowrap ${st.cls}`}>{st.label}</span>
        </div>
      </button>
    );
  };

  if (loading) return <div className="flex items-center justify-center h-full"><div className="text-atlasCyan animate-pulse">{t('common.loading')}</div></div>;

  return (
    <div data-testid="engagements-page" className="p-6 lg:p-8 max-w-3xl mx-auto pb-24">
      {/* Header */}
      <div className="flex items-center gap-3 mb-6">
        <button onClick={handleBack} data-testid="eng-back-btn" className="p-2 rounded-lg text-atlasTextSecondary hover:text-white hover:bg-white/5 transition-all">
          <ArrowLeft className="w-5 h-5" />
        </button>
        <div className="flex-1">
          <h1 className="text-2xl font-bold text-white">{t('engagements.title')}</h1>
          <p className="text-sm text-atlasTextSecondary">{t('engagements.subtitle')}</p>
        </div>
      </div>

      {/* Today */}
      {data.today.length > 0 && (
        <div className="mb-6">
          <h2 className="text-sm font-semibold text-atlasCyan mb-3 flex items-center gap-2"><Clock className="w-4 h-4" /> {t('engagements.today')}</h2>
          <div className="space-y-2">{data.today.map(e => <EngCard key={e.id} eng={e} section="today" />)}</div>
        </div>
      )}

      {/* Upcoming */}
      {data.upcoming.length > 0 && (
        <div className="mb-6">
          <h2 className="text-sm font-semibold text-white mb-3 flex items-center gap-2"><CalendarClock className="w-4 h-4 text-atlasTextSecondary" /> {t('engagements.upcoming')}</h2>
          <div className="space-y-2">{data.upcoming.map(e => <EngCard key={e.id} eng={e} section="upcoming" />)}</div>
        </div>
      )}

      {/* Empty state */}
      {data.today.length === 0 && data.upcoming.length === 0 && data.history.length === 0 && (
        <div className="glass-card p-10 text-center mb-6">
          <CalendarClock className="w-10 h-10 text-atlasCyan mx-auto mb-4" />
          <p className="text-white font-medium mb-1">{t('engagements.emptyTitle')}</p>
          <p className="text-sm text-atlasTextSecondary">{t('engagements.emptyDesc')}</p>
        </div>
      )}

      {/* History */}
      {data.history.length > 0 && (
        <div className="mb-6">
          <button onClick={() => setExpandHistory(!expandHistory)} className="flex items-center gap-2 text-sm font-semibold text-atlasTextSecondary mb-3 hover:text-white transition-colors">
            {expandHistory ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
            {t('engagements.history')} ({data.history.length})
          </button>
          {expandHistory && <div className="space-y-2">{data.history.map(e => <EngCard key={e.id} eng={e} section="history" />)}</div>}
        </div>
      )}

      {/* FAB Create */}
      <button data-testid="eng-create-fab" onClick={() => openCreate()} className="fixed z-40 bottom-24 lg:bottom-8 right-6 w-14 h-14 rounded-full bg-atlasCyan flex items-center justify-center shadow-lg shadow-atlasCyan/25 hover:scale-110 active:scale-95 transition-all">
        <Plus className="w-6 h-6 text-atlasBg" />
      </button>

      {/* Create/Edit Modal */}
      {showCreate && (
        <div className="fixed inset-0 z-[100] bg-atlasBg flex flex-col" style={{ paddingBottom: 'env(safe-area-inset-bottom, 0px)' }}>
          <div className="flex-shrink-0 flex items-center justify-between p-4 border-b border-atlasBorder">
            <h2 className="text-lg font-semibold text-white">{editId ? t('engagements.edit') : t('engagements.new')}</h2>
            <button onClick={() => setShowCreate(false)} className="p-2 rounded-lg text-atlasTextSecondary hover:text-white"><X className="w-5 h-5" /></button>
          </div>
          <div className="flex-1 overflow-y-auto px-6 py-4 space-y-5">
            <div>
              <label className="text-sm text-atlasTextSecondary mb-1 block">{t('engagements.whatCommitment')}</label>
              <textarea data-testid="eng-desc-input" value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} maxLength={280}
                placeholder={t('engagements.descPlaceholder')} rows={3}
                className="w-full bg-atlasCard border border-atlasBorder rounded-xl px-4 py-3 text-white text-sm resize-none focus:outline-none focus:border-atlasCyan/50 placeholder:text-atlasTextSecondary" />
              <p className="text-xs text-atlasTextSecondary mt-1">{form.description.length}/280</p>
            </div>
            <div>
              <label className="text-sm text-atlasTextSecondary mb-1 block">{t('engagements.date')}</label>
              <Input data-testid="eng-date-input" type="date" value={form.date_engagement} onChange={e => setForm({ ...form, date_engagement: e.target.value })} className="input-atlas" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-sm text-atlasTextSecondary mb-1 block">{t('engagements.startTime')}</label>
                <Input data-testid="eng-start-input" type="time" value={form.heure_debut} onChange={e => setForm({ ...form, heure_debut: e.target.value })} className="input-atlas" />
              </div>
              <div>
                <label className="text-sm text-atlasTextSecondary mb-1 block">{t('engagements.endTime')}</label>
                <Input data-testid="eng-end-input" type="time" value={form.heure_fin} onChange={e => setForm({ ...form, heure_fin: e.target.value })} className="input-atlas" />
              </div>
            </div>
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <div><p className="text-white text-sm">{t('engagements.reminderToggle')}</p><p className="text-xs text-atlasTextSecondary">{t('engagements.reminderHint')}</p></div>
                <button onClick={() => setForm({ ...form, rappel_actif: !form.rappel_actif })}
                  className={`relative w-12 h-6 rounded-full transition-colors ${form.rappel_actif ? 'bg-atlasCyan' : 'bg-atlasBorder'}`}>
                  <span className={`absolute top-0.5 w-5 h-5 rounded-full bg-white transition-transform ${form.rappel_actif ? 'translate-x-6' : 'translate-x-0.5'}`} />
                </button>
              </div>
              <div className="flex items-center justify-between">
                <div><p className="text-white text-sm">{t('engagements.justifToggle')}</p><p className="text-xs text-atlasTextSecondary">{t('engagements.justifHint')}</p></div>
                <button onClick={() => setForm({ ...form, justification_vocale_active: !form.justification_vocale_active })}
                  className={`relative w-12 h-6 rounded-full transition-colors ${form.justification_vocale_active ? 'bg-atlasCyan' : 'bg-atlasBorder'}`}>
                  <span className={`absolute top-0.5 w-5 h-5 rounded-full bg-white transition-transform ${form.justification_vocale_active ? 'translate-x-6' : 'translate-x-0.5'}`} />
                </button>
              </div>
            </div>
            {/* Spacer for scroll */}
            <div className="h-4" />
          </div>
          <div className="flex-shrink-0 p-6 flex gap-3 border-t border-atlasBorder bg-atlasBg/90 backdrop-blur-lg" style={{ paddingBottom: 'max(1.5rem, env(safe-area-inset-bottom, 1.5rem))' }}>
            <Button variant="outline" onClick={() => setShowCreate(false)} className="flex-1 border-atlasTextSecondary text-atlasTextSecondary">{t('common.cancel')}</Button>
            <Button data-testid="eng-save-btn" onClick={handleSave} disabled={saving} className="flex-1 btn-primary">{saving ? '...' : (editId ? t('engagements.save') : t('engagements.create'))}</Button>
          </div>
        </div>
      )}

      {/* Detail Modal */}
      {showDetail && (
        <div className="fixed inset-0 z-50 bg-atlasBg/90 backdrop-blur-sm flex items-center justify-center p-6">
          <div className="glass-card p-6 w-full max-w-md">
            <div className="flex justify-between items-start mb-4">
              <div>
                <p className="text-xs text-atlasTextSecondary">{formatDateFR(showDetail.date_engagement)} · {showDetail.heure_debut}{showDetail.heure_fin ? ` - ${showDetail.heure_fin}` : ''}</p>
                <p className="text-white font-semibold mt-1">{showDetail.description}</p>
                <span className={`inline-block mt-2 text-xs px-2 py-0.5 rounded-full border ${(STATUS_CONFIG[showDetail.statut] || STATUS_CONFIG.a_venir).cls}`}>
                  {(STATUS_CONFIG[showDetail.statut] || STATUS_CONFIG.a_venir).label}
                </span>
              </div>
              <button onClick={() => setShowDetail(null)} className="p-1 text-atlasTextSecondary hover:text-white"><X className="w-5 h-5" /></button>
            </div>
            <div className="flex flex-wrap gap-2 mt-4">
              {['a_venir', 'en_cours'].includes(showDetail.statut) && (
                <>
                  <Button size="sm" onClick={() => updateStatus(showDetail.id, 'complete')} className="bg-green-600 hover:bg-green-500 text-white text-xs">
                    <CheckCircle2 className="w-3 h-3 mr-1" />{t('engagements.markComplete')}
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => updateStatus(showDetail.id, 'reporte')} className="text-orange-400 border-orange-500/30 text-xs">
                    <RotateCcw className="w-3 h-3 mr-1" />{t('engagements.postpone')}
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => openEdit(showDetail)} className="text-atlasTextSecondary border-atlasBorder text-xs">
                    <Edit2 className="w-3 h-3 mr-1" />{t('engagements.modify')}
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => updateStatus(showDetail.id, 'annule')} className="text-red-400 border-red-500/30 text-xs">
                    <Ban className="w-3 h-3 mr-1" />{t('engagements.cancelEng')}
                  </Button>
                </>
              )}
              {['complete', 'reporte', 'ignore', 'annule'].includes(showDetail.statut) && (
                <Button size="sm" variant="outline" onClick={() => { openCreate({ description: showDetail.description, date_engagement: tomorrow(), heure_debut: showDetail.heure_debut, heure_fin: showDetail.heure_fin || '', rappel_actif: true, justification_vocale_active: true }); setShowDetail(null); }}
                  className="text-atlasCyan border-atlasCyan/30 text-xs">
                  <Copy className="w-3 h-3 mr-1" />{t('engagements.redoSimilar')}
                </Button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Push permission modal (Phase B1) */}
      <PushPermissionModal
        open={showPushModal}
        onEnable={handleEnablePush}
        onLater={handleLaterPush}
        loading={pushSubscribing}
      />
    </div>
  );
};

export default Engagements;
